// RFC 7807 Problem Detail — matches Spring's ProblemDetail response shape
export interface ProblemDetail {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  errors?: Record<string, string>;
}

// Wizard step identifiers for review flow
export type WizardStep = 'welcome' | 'outside' | 'outside-hard' | 'inside' | 'interior' | 'preview3d' | 'confirm';

// Cover summary for yearbook info
export interface CoverSummary {
  coverId: number;
  coverType: string;
  coverStyle: string | null;
  optionCount: number;
  guideConfigId: number | null;
  guideConfigName: string | null;
}

// Content area readiness for admin dashboard badges
export interface ContentArea {
  type: string;
  label: string;
  ready: boolean;
}

// Yearbook info for admin dashboard
export interface YearbookInfo {
  yearbookId: number;
  clientNumber: string | null;
  schoolName: string;
  year: number;
  covers: CoverSummary[];
  versionCount: number;
  physicalProofCost: number | null;
  contentAreas: ContentArea[];
}

// Review Version Status
export enum VersionStatus {
  Created = 'created',
  Sent = 'sent',
  Opened = 'opened',
  InApprovalProgress = 'in_approval_progress',
  Approved = 'approved',
  ApprovedWithPhysical = 'approved_with_physical',
  Rejected = 'rejected',
  StaffOverride = 'staff_override',
}

// Response Type - what the school chooses
export enum ResponseType {
  Approve = 'approve',
  Reject = 'reject',
  StaffOverride = 'staff_override',
}

// Per-step decision tracking for wizard flow
export interface StepDecision {
  type: 'approve' | 'reject';
  comments: string | null;
}

// Cover surface types
export type SurfaceType = 'front' | 'back' | 'inside-front' | 'inside-back';

// Cover types
export type CoverType = 'soft' | 'hard';

// Per-option guide types (matches Canvas Builder guide system)
export type GuideType =
  | 'cut_line'
  | 'edge_line'
  | 'safe_area'
  | 'safe_area_back'
  | 'safe_area_front'
  | 'spine'
  | 'center_lines'
  | 'hinge_line'
  | 'glue_area'
  | 'bg_offset_inside_front'
  | 'bg_offset_inside_back';

// Surface view discriminator for guides
export type SurfaceView = 'outside' | 'inside';

// Per-option guide data from backend
export interface CoverOptionGuide {
  id?: number;
  guideType: GuideType;
  isVisible: boolean;
  guideColor: string;
  offsetTopInches: number | null;
  offsetRightInches: number | null;
  offsetBottomInches: number | null;
  offsetLeftInches: number | null;
  spineWidthInches: number | null;
  surfaceView: SurfaceView | null;
}

// Review view types (tabs in the review component)
export type ReviewView = 'outside' | 'inside' | 'guts';

// Guts upload status
export type GutsUploadStatus = 'uploading' | 'processing' | 'ready' | 'failed';

// Guts page layout — how the source PDF was formatted
export type GutsPageLayout = 'SINGLE' | 'SPREAD';

// Individual guts page (interior yearbook page)
// URLs are built dynamically with token on the frontend
export interface GutsPage {
  id: number;
  pageNumber: number;
  widthPx: number;
  heightPx: number;
}

/**
 * Product guide trim/bleed dimensions (from hc_product_guides table).
 *
 * ## Bleed offset model
 *
 * Backend images include bleed area around the final trim size. The default
 * bleed on each side is derived symmetrically:
 *
 *   defaultLR = (imageWidthInches - trimWidthInches) / 2
 *   defaultTB = (imageHeightInches - trimHeightInches) / 2
 *
 * where imageWidthInches = page.widthPx / 300 (backend renders at 300 DPI).
 *
 * The per-side bleed fields (`bleedTopInches`, etc.) are **offsets** (deltas)
 * added to the symmetric default — they are NOT absolute distances:
 *
 *   effectiveBleed = default + bleedOffset
 *
 * | Value    | Meaning                                           |
 * |----------|---------------------------------------------------|
 * | null / 0 | No adjustment — use symmetric default              |
 * | +0.125   | Trim 0.125" MORE than default on that side         |
 * | -0.0625  | Trim 0.0625" LESS than default on that side        |
 *
 * This is designed for machine-print adjustments where the cutter doesn't
 * land exactly on the symmetric trim line.
 *
 * ## How bleed is applied
 *
 * **2D guts viewer** (guts-viewer.component.ts):
 *   Uses CSS overflow clipping. The page container is sized to the visible
 *   area (image minus bleed on each side). The <img> is absolutely positioned
 *   larger than its container (width/height > 100%) with negative top/left
 *   offsets so the bleed overflows and gets clipped by `overflow: hidden`.
 *
 * **3D book viewer** (book-viewer-3d.component.ts → computeGutsCrop):
 *   Uses Three.js UV texture repeat/offset. `repeatX/Y` controls the crop
 *   window size as a fraction of the full texture (< 1 = show less than the
 *   full image). `offsetX/Y` shifts the crop window origin in UV space
 *   (0,0 = bottom-left of texture).
 *
 * Both paths use the same effective bleed formula above to stay in sync.
 *
 * ## Glue strip
 *
 * `glueStripInches` is the width of the area glued to the cover spine on the
 * first and last interior pages. It is subtracted from the visible width on
 * the spine side of those pages only (not an offset — an absolute value).
 * Defaults to GLUE_STRIP_INCHES (0.35") when null.
 */
export interface GutsTrimGuide {
  trimWidthInches: number;
  trimHeightInches: number;
  bleedTopInches: number | null;
  bleedRightInches: number | null;
  bleedBottomInches: number | null;
  bleedLeftInches: number | null;
  glueStripInches: number | null;
}

// Guts upload data (interior pages PDF)
export interface GutsData {
  uploadId: number;
  versionId: number | null;
  versionNumber: number;
  originalFilename: string | null;
  status: GutsUploadStatus;
  pageLayout: GutsPageLayout | null;
  pageCount: number;
  processingProgress: number;
  errorMessage: string | null;
  pages: GutsPage[];
  gutsTrimGuide: GutsTrimGuide | null;
}

// Review Session - links to a yearbook
export interface ReviewVersion {
  id: number;
  yearbookId: number;
  versionNumber: number;
  reviewToken: string;
  statusCode: VersionStatus;
  isLocked: boolean;
  hasGuts: boolean;
  sentToEmail: string | null;
  sentToName: string | null;
  sentAt: string | null;
  firstOpenedAt: string | null;
  lastOpenedAt: string | null;
  responseTypeCode: ResponseType | null;
  outsideDecision: string | null;
  outsideHardDecision: string | null;
  insideDecision: string | null;
  interiorDecision: string | null;
  wantsPhysicalCopy: boolean | null;
  reviewedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  createdBy: string | null;
}

// Surface with thumbnail for display
export interface ReviewSurface {
  id: number;       // Surface ID for fetching canvas JSON
  type: SurfaceType;
  thumbnail: string; // base64 data URL (low quality preview)
}

// Cover option with its surfaces and guides
export interface ReviewOption {
  id: number;
  name: string;
  optionIndex: number;
  coverType: CoverType;
  thumbnailUrl: string | null;
  surfaces: ReviewSurface[];
  guides: CoverOptionGuide[];
  guideConfigId: number | null;
  canvasWidthInches: number | null;
  canvasHeightInches: number | null;
}

// School information
export interface SchoolInfo {
  name: string;
  yearbookId: number;
  yearbookTitle: string;
}

// Legacy guide settings (kept for backward compat during transition)
export interface GuideSettings {
  spineWidthInches: number;
  bleedSizeInches: number;
  safeAreaMarginInches: number;
  showBleedLine: boolean;
  showCutLine: boolean;
  showSafeArea: boolean;
  showSpine: boolean;
}

// Glue strip: area along the spine edge of the first/last interior pages
// that is glued to the cover and not visible when the book is open
export const GLUE_STRIP_INCHES = 0.35; // half inch

// Spine buffer: when the backend splits a spread PDF (17.5") into individual
// pages, it extends each half 0.25" past the center to produce 9"-wide images.
// This overlap contains content from the adjacent page and must be fully cropped
// on the spine side. For single-page PDFs this value is unused.
export const SPREAD_SPINE_BUFFER_INCHES = 0.25;

// Server-persisted review progress (step decisions saved as school reviews)
export interface ReviewProgress {
  currentStep: string;
  furthestStepIndex: number | null;
  outsideDecision: string | null;
  outsideComments: string | null;
  outsideHardDecision: string | null;
  outsideHardComments: string | null;
  insideDecision: string | null;
  insideComments: string | null;
  interiorDecision: string | null;
  interiorComments: string | null;
}

// Full review data returned when accessing review link
export interface ReviewData {
  version: ReviewVersion;
  school: SchoolInfo;
  coverType: CoverType | null;
  coverTypes: CoverType[];
  guideSettings: GuideSettings;
  options: ReviewOption[];
  existingResponse: ReviewResponse | null;
  guts: GutsData | null;
  physicalCopyCost: number | null;
  postPhysicalChangeCost: number | null;
  additionalProofReviewFee: number | null;
  changesAfterProofApprovedFee: number | null;
  complimentaryProofReviews: number | null;
  progress: ReviewProgress | null;
}

// Physical proof order shipping address
export interface ShippingAddress {
  orderNumber?: number;
  shippingName: string;
  shippingAddress: string;
  shippingCity: string;
  shippingState: string;
  shippingZip: string;
  quantity: number;
  notes: string | null;
}

// Per-option feedback from the reviewer
export interface OptionFeedback {
  optionId: number;
  rating: number | null; // 1-5 stars, optional
  comments: string | null;
  isPreferred: boolean;
}

// Submission request sent to backend
export interface ReviewSubmission {
  responseTypeCode: ResponseType;
  outsideComments: string | null;
  outsideHardComments: string | null;
  insideComments: string | null;
  interiorComments: string | null;
  outsideDecision: string | null;
  outsideHardDecision: string | null;
  insideDecision: string | null;
  interiorDecision: string | null;
  approverName: string;
  approverEmail: string;
  approverRole: string | null;
  wantsPhysicalCopy: boolean;
  shippingAddress: ShippingAddress | null;
}

// Response stored after submission
export interface ReviewResponse {
  id: number;
  responseTypeCode: ResponseType;
  generalComments: string | null;
  outsideComments: string | null;
  outsideHardComments: string | null;
  insideComments: string | null;
  interiorComments: string | null;
  overrideReason: string | null;
  outsideDecision: string | null;
  outsideHardDecision: string | null;
  insideDecision: string | null;
  interiorDecision: string | null;
  approverName: string;
  approverEmail: string;
  approverRole: string | null;
  wantsPhysicalCopy: boolean;
  shippingAddress: ShippingAddress | null;
  submittedAt: string;
}

// Result of submission
export interface SubmissionResult {
  success: boolean;
  message: string;
  confirmationNumber: string;
}

// Advisor contact from tp_yearbooks_data (advisors 1-5)
export interface AdvisorContact {
  name: string;
  email: string;
}

// Create session request (for admin/yearbook management)
export interface CreateVersionRequest {
  yearbookId: number;
  sentToEmail: string;
  sentToName: string;
  expiresInDays?: number;
}

export interface StaffOverrideRequest {
  reason: string;
}

// Session response with review URL
export interface VersionResponse extends ReviewVersion {
  reviewUrl: string;
}

// Session detail for admin drawer (includes response info)
export interface VersionDetail {
  version: ReviewVersion;
  response: ReviewResponse | null;
}

// Proof link for multi-link tracking
export interface ProofLink {
  id: number;
  versionId: number;
  token: string;
  label: string;
  isActive: boolean;
  viewCount: number;
  lastAccessedAt: string | null;
  createdAt: string;
  createdBy: string | null;
  deactivatedAt: string | null;
}

// Physical proof order summary (yearbook-level, includes legacy orders)
export interface PhysicalProofOrderSummary {
  id: number;
  orderNumber: number;
  quantity: number;
  statusCode: string;
  statusName: string;
  versionId: number | null;
  versionNumber: number | null;
  shippingName: string;
  shippingAddress: string;
  shippingCity: string;
  shippingState: string;
  shippingZip: string;
  trackingNumber: string | null;
  createdAt: string;
}

